// The day's plan. At most one budget change per campaign.
//
// Every action shows two figures rather than one: what it is expected to make,
// and what it makes on a bad day. That pair is the feature's whole promise — not
// "we think this pays" but "this cannot cost you more than it might make".
//
// A probe is shown as a probe. It is a test sized to settle a question, not a
// smaller version of the change, and calling it the change would be taking the
// decision without proving it.
import React, { useState } from 'react';
import { FiCheck, FiRotateCcw, FiX } from 'react-icons/fi';
import s from '../BudgetOptimiser.module.css';
import { OWNER_LABEL, money } from '../format';

function Gain({ action, currency }) {
  if (action.expected_gain == null && action.worst_case == null) return null;
  return (
    <div className={s.gainRow}>
      {action.expected_gain != null && (
        <span><span className={s.gainLabel}>Expected </span>
          <span className={s.gain}>{money(action.expected_gain, currency)}</span></span>
      )}
      {action.worst_case != null && (
        <span><span className={s.gainLabel}>On a bad day </span>
          <span className={s.worst}>{money(action.worst_case, currency)}</span></span>
      )}
    </div>
  );
}

function Budget({ action, currency }) {
  if (action.amount == null) return null;
  return (
    <div className={s.budgetChange}>
      {action.from_amount != null && (
        <>
          <span className={s.budgetFrom}>{money(action.from_amount, currency)}</span>
          <span className={s.budgetArrow}>→</span>
        </>
      )}
      <span>{money(action.amount, currency)}</span>
      <span className={s.gainLabel} style={{ fontSize: 12 }}>a day</span>
    </div>
  );
}

function Expected({ expected, currency }) {
  if (!expected) return null;
  const bits = [];
  const add = (label, value) => value != null && bits.push([label, value]);
  add('Extra a month', expected.extra_spend_per_month != null
    ? money(expected.extra_spend_per_month, currency) : null);
  add('Saves a month', expected.saves_per_month != null
    ? money(expected.saves_per_month, currency) : null);
  add('Saves this month', expected.saves_this_month != null
    ? money(expected.saves_this_month, currency) : null);
  add('Moves a day', expected.moves_per_day != null
    ? money(expected.moves_per_day, currency) : null);
  add('To', expected.to);
  add('From', expected.from);
  add('Gives up', expected.sales_given_up ? money(expected.sales_given_up, currency)
    : null);
  add('Frees a day', expected.frees_per_day != null
    ? money(expected.frees_per_day, currency) : null);
  add('Restock', expected.restock_date);
  add('Event days', expected.event_days);
  add('Reverts on', expected.reverts_on || expected.restored_on);
  if (expected.missed_sales_last_14_days) {
    bits.push(['Missed in 14 days',
      `${money(expected.missed_sales_last_14_days.low, currency)}–${money(expected.missed_sales_last_14_days.high, currency)}`]);
  }
  if (!bits.length) return null;
  return (
    <div className={s.sugExpected}>
      {bits.map(([label, value]) => (
        <span key={label}>{label} <b>{value}</b></span>
      ))}
    </div>
  );
}

export default function Actions({ data, currency, onDecide, onApply, onUndo,
                                  onApproveMany, busy }) {
  const [owner, setOwner] = useState('all');
  const [showing, setShowing] = useState('open');
  const [selected, setSelected] = useState([]);

  const all = data?.actions || [];
  const open = all.filter((a) => ['Pending', 'Probe'].includes(a.status));
  const decided = all.filter((a) => !['Pending', 'Probe'].includes(a.status));
  const pool = showing === 'open' ? open : decided;
  const rows = pool.filter((a) => owner === 'all' || a.owner === owner);

  const owners = ['all', 'revert', 'stock', 'event', 'ceiling', 'reallocate', 'cut',
                  'rescue'];
  const counts = { all: open.length };
  owners.slice(1).forEach((o) => {
    counts[o] = open.filter((a) => a.owner === o).length;
  });

  const toggle = (id) => setSelected((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  if (!all.length) {
    return (
      <div>
        {data?.principle && <p className={s.principle}>{data.principle}</p>}
        <p className={s.empty}>
          Nothing to change today. {(data?.notes || []).join(' ')} {data?.gates?.note}
        </p>
      </div>
    );
  }

  return (
    <div>
      {data?.principle && <p className={s.principle}>{data.principle}</p>}
      {data?.apply_note && (
        <div className={s.applyBanner}>
          <b>Approved changes are not being sent to Amazon.</b> {data.apply_note}
          {!!(data.apply_blocked_by || []).length && (
            <ul className={s.lockList}>
              {data.apply_blocked_by.map((lock) => <li key={lock}>{lock}</li>)}
            </ul>
          )}
        </div>
      )}
      {(data?.notes || []).map((note) => (
        <p key={note} className={s.coverageWarn}>{note}</p>
      ))}

      <div className={s.filterRow}>
        {[['open', `To decide (${open.length})`],
          ['decided', `Decided (${decided.length})`]].map(([key, label]) => (
          <button key={key} className={showing === key ? s.chipActive : s.chip}
                  onClick={() => { setShowing(key); setSelected([]); }}>
            {label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#6B7280' }}>
          {money(data?.total_gain, currency)} expected across today's plan
        </span>
      </div>

      <div className={s.filterRow}>
        {owners.filter((o) => o === 'all' || counts[o] > 0 || showing === 'decided')
          .map((o) => (
            <button key={o} className={owner === o ? s.chipActive : s.chip}
                    onClick={() => setOwner(o)}>
              {o === 'all' ? 'Everything' : OWNER_LABEL[o]}
              {showing === 'open' && <span className={s.chipCount}>{counts[o]}</span>}
            </button>
          ))}
      </div>

      {showing === 'open' && selected.length > 0 && (
        <div className={s.bulkBar}>
          <span className={s.bulkCount}>{selected.length} selected</span>
          <button className={s.approve} disabled={busy}
                  onClick={() => { onApproveMany(selected); setSelected([]); }}>
            <FiCheck /> Approve {selected.length}
          </button>
          <button className={s.linkButton} onClick={() => setSelected([])}>Clear</button>
        </div>
      )}

      <div className={s.findingList}>
        {rows.map((a) => (
          <article key={a.id}
                   className={`${s.suggestion} ${s[`suggestion_${a.status === 'Probe'
                     ? 'Watch' : a.priority}`]} ${s[`suggestion_${a.status}`] || ''}`}>
            <div className={s.sugHead}>
              {showing === 'open' && (
                <input type="checkbox" checked={selected.includes(a.id)}
                       onChange={() => toggle(a.id)} aria-label="select" />
              )}
              <span className={`${s.rankPill} ${s[`rankPill_${a.owner}`] || ''}`}>
                {OWNER_LABEL[a.owner] || a.owner}
              </span>
              <span className={s.sugHeadline}>{a.headline}</span>
              <Budget action={a} currency={currency} />
            </div>

            <p className={s.sugWhy}>{a.why}</p>
            <Expected expected={a.expected} currency={currency} />
            <Gain action={a} currency={currency} />

            {a.status === 'Probe' && (
              <div className={s.sugWatch}>
                This is a test rather than the whole change. If it holds up, the rest
                follows; if it does not, it is reversed.
              </div>
            )}
            {a.linked && (
              <div className={s.linkedNote}>
                Approved together with its other half — half a move takes money from
                one campaign and gives it to nobody.
              </div>
            )}
            {(a.warnings || []).map((w) => (
              <div key={w} className={s.sugWarn}>{w}</div>
            ))}
            {a.expired_why && <div className={s.sugWarn}>{a.expired_why}</div>}
            {a.failure && (
              <div className={s.sugWarn}>
                Amazon did not accept this: {a.failure.reason}
              </div>
            )}

            <div className={s.sugMeta}>
              {!['Pending', 'Probe'].includes(a.status) && (
                <span className={`${s.statusPill} ${s[`statusPill_${a.status}`] || ''}`}>
                  {a.status}
                </span>
              )}
              {['Pending', 'Probe'].includes(a.status) && (
                <span>{a.priority} priority</span>
              )}
              {a.also_because && <span>also because of {a.also_because}</span>}
              <span className={s.sugActions}>
                {['Pending', 'Probe'].includes(a.status) && (
                  <>
                    <button className={s.approve} disabled={busy}
                            onClick={() => onDecide(a, 'approve')}>
                      <FiCheck /> Approve
                    </button>
                    <button className={s.linkButton} disabled={busy}
                            onClick={() => onDecide(a, 'dismiss')}>
                      <FiX /> Not this one
                    </button>
                  </>
                )}
                {a.status === 'Approved' && (
                  <button className={s.linkButton} disabled={busy}
                          onClick={() => onApply(a)}>
                    See what would be sent
                  </button>
                )}
                {a.status === 'Applied' && (
                  <button className={s.linkButton} disabled={busy}
                          onClick={() => onUndo(a)}>
                    <FiRotateCcw /> Undo
                  </button>
                )}
                {['Dismissed', 'Expired'].includes(a.status) && (
                  <button className={s.linkButton} disabled={busy}
                          onClick={() => onDecide(a, 'reopen')}>
                    <FiRotateCcw /> Bring back
                  </button>
                )}
              </span>
            </div>
          </article>
        ))}
        {!rows.length && <p className={s.empty}>Nothing here.</p>}
      </div>

      {!!(data?.dropped || []).length && (
        <>
          <h3 className={s.sectionTitle}>Considered and set aside</h3>
          <p className={s.coverage}>
            One budget change per campaign at a time, so you can see what it did before
            the next one.
          </p>
          {data.dropped.slice(0, 12).map((d, index) => (
            <div key={`${d.id}-${index}`} className={s.droppedRow}>
              <b>{d.headline}</b> — {d.dropped_why}
            </div>
          ))}
        </>
      )}

      {data?.approval_note && (
        <p className={s.coverage} style={{ marginTop: 16 }}>{data.approval_note}</p>
      )}
    </div>
  );
}
